import { useEffect, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import BranchNav from "@/components/BranchNav";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { resolveImageUrl } from "@/components/ImageUploader";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1601050690597-df0568f70950?auto=format&fit=crop&w=900&q=80";

export default function Distribution() {
  const { tr, lang } = useLang();
  const [products, setProducts] = useState([]);
  const [points, setPoints] = useState([]);
  const [site, setSite] = useState({});
  const [b2bForm, setB2bForm] = useState({
    business_name: "",
    contact_name: "",
    email: "",
    phone: "",
    nif: "",
    address: "",
    estimated_volume: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [b2bOpen, setB2bOpen] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/collection-points").then((r) => setPoints(r.data)).catch(() => {});
    api.get("/site-content").then((r) => setSite(r.data)).catch(() => {});
    document.title = "Lama Distribution — Mercearia Mediterrânica";
  }, []);

  async function submitB2b(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/wholesale-requests", b2bForm);
      toast.success(tr("dist.formSent"));
      setB2bForm({ business_name: "", contact_name: "", email: "", phone: "", nif: "", address: "", estimated_volume: "", message: "" });
      setB2bOpen(false);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain" data-testid="page-distribution">
      <BranchNav branch="distribution" />

      {/* HERO */}
      <section className="px-6 md:px-10 pt-16 pb-16 max-w-7xl mx-auto" data-testid="dist-hero">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#8C7A6B] mb-6">
              {tr("dist.heroOverline")}
            </div>
            <h1 className="font-serif text-5xl md:text-7xl font-light leading-[0.95] tracking-tight whitespace-pre-line text-[#1A1A1A]">
              {tr("dist.heroTitle")}
            </h1>
            <p className="mt-8 max-w-xl text-base md:text-lg text-[#1A1A1A]/75 leading-relaxed">
              {lang === "pt" ? site.distribution_intro_pt : site.distribution_intro_en}
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href="#pricing"
                className="inline-flex items-center gap-3 border border-[#1A1A1A] px-8 py-4 text-[11px] uppercase tracking-[0.3em] hover:bg-[#1A1A1A] hover:text-[#FDFBF7] transition-colors"
                data-testid="cta-pricing"
              >
                {tr("dist.pricingOverline")}
              </a>
              <Button
                onClick={() => setB2bOpen(true)}
                className="bg-[#1A1A1A] text-[#FDFBF7] hover:bg-black rounded-none px-8 py-4 text-[11px] uppercase tracking-[0.3em] h-auto"
                data-testid="cta-b2b-open"
              >
                {tr("dist.requestB2b")}
              </Button>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img
              src="https://images.unsplash.com/photo-1634657443172-efbae44fd04b?auto=format&fit=crop&w=900&q=80"
              alt="Mediterranean ingredients"
              className="w-full h-[420px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* MASTER PRICING TABLE */}
      <section id="pricing" className="px-6 md:px-10 py-20 bg-[#F4F1EA] border-y border-[#E5E0D8]">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-baseline justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#8C7A6B] mb-3">
                {tr("dist.pricingOverline")}
              </div>
              <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-[#1A1A1A]">
                {tr("dist.pricingTitle")}
              </h2>
            </div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#8C7A6B]">EUR · IVA incl.</div>
          </div>

          <div className="overflow-x-auto border border-[#1A1A1A] bg-[#FDFBF7]" data-testid="pricing-table-wrap">
            <Table className="min-w-[760px]">
              <TableHeader>
                <TableRow className="border-b border-[#1A1A1A] hover:bg-transparent">
                  <TableHead className="text-[#1A1A1A] text-[10px] uppercase tracking-[0.25em] py-4">{tr("dist.colProduct")}</TableHead>
                  <TableHead className="text-[#1A1A1A] text-[10px] uppercase tracking-[0.25em]">{tr("dist.colUnit")}</TableHead>
                  <TableHead className="text-[#1A1A1A] text-[10px] uppercase tracking-[0.25em]">{tr("dist.colOrigin")}</TableHead>
                  <TableHead className="text-right text-[#1A1A1A] text-[10px] uppercase tracking-[0.25em]">{tr("dist.colRetail")}</TableHead>
                  <TableHead className="text-right text-[#1A1A1A] text-[10px] uppercase tracking-[0.25em]">{tr("dist.colWholesale")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow key={p.id} className="border-b border-[#E5E0D8] hover:bg-[#EAE5DC] transition-colors" data-testid={`pricing-row-${p.id}`}>
                    <TableCell className="font-serif text-lg text-[#1A1A1A] py-4">
                      {lang === "pt" ? p.name_pt : p.name_en}
                    </TableCell>
                    <TableCell className="text-sm text-[#1A1A1A]/70 font-mono">{p.unit}</TableCell>
                    <TableCell className="text-sm text-[#1A1A1A]/70">{p.origin}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-[#1A1A1A]">€ {Number(p.price_retail).toFixed(2)}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-[#1A1A1A]">€ {Number(p.price_wholesale).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                {products.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-[#1A1A1A]/50 py-10">
                      {lang === "pt" ? "Sem produtos no momento." : "No products yet."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </section>

      {/* CATALOG */}
      <section id="catalog" className="px-6 md:px-10 py-24 max-w-7xl mx-auto">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#8C7A6B] mb-4">
          {tr("dist.catalogOverline")}
        </div>
        <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-[#1A1A1A] mb-12">
          {tr("dist.catalogTitle")}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14" data-testid="product-grid">
          {products.map((p) => (
            <ProductCard key={p.id} p={p} lang={lang} tr={tr} />
          ))}
        </div>
      </section>

      {/* COLLECTION POINTS */}
      <section id="collection" className="px-6 md:px-10 py-24 bg-[#1A1A1A] text-[#FDFBF7]">
        <div className="max-w-7xl mx-auto">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8C7A6B] mb-4">
            {tr("dist.collectionOverline")}
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight mb-12">
            {tr("dist.collectionTitle")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10" data-testid="collection-points">
            {points.map((pt) => (
              <div key={pt.id} className="bg-[#1A1A1A] p-8">
                <div className="font-serif text-2xl mb-3">{pt.name}</div>
                <div className="text-sm text-[#FDFBF7]/70 space-y-1.5">
                  <div className="flex gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0" /> {pt.address}{pt.city && `, ${pt.city}`}</div>
                  {pt.hours && <div className="flex gap-2"><Clock className="w-4 h-4 mt-0.5 shrink-0" /> {pt.hours}</div>}
                  {pt.phone && <div className="pl-6">{pt.phone}</div>}
                </div>
              </div>
            ))}
            {points.length === 0 && (
              <div className="col-span-3 p-12 text-center text-[#FDFBF7]/50">
                {lang === "pt" ? "Sem pontos de recolha." : "No pickup points yet."}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* B2B CTA banner */}
      <section id="b2b" className="px-6 md:px-10 py-24 max-w-5xl mx-auto text-center">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#8C7A6B] mb-4">B2B</div>
        <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tight text-[#1A1A1A]">
          {tr("dist.b2bTitle")}
        </h2>
        <p className="mt-6 max-w-xl mx-auto text-base md:text-lg text-[#1A1A1A]/75">
          {tr("dist.b2bLead")}
        </p>
        <Button
          onClick={() => setB2bOpen(true)}
          className="mt-10 bg-[#1A1A1A] text-[#FDFBF7] hover:bg-black rounded-none px-10 py-6 text-[11px] uppercase tracking-[0.3em]"
          data-testid="cta-b2b-open-bottom"
        >
          {tr("dist.requestB2b")}
        </Button>
      </section>

      {/* B2B Dialog */}
      <Dialog open={b2bOpen} onOpenChange={setB2bOpen}>
        <DialogContent className="max-w-2xl bg-[#FDFBF7] rounded-none border border-[#1A1A1A]" data-testid="b2b-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl font-light text-[#1A1A1A]">
              {tr("dist.b2bTitle")}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#1A1A1A]/60">
              {tr("dist.b2bLead")}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitB2b} className="space-y-5 mt-4" data-testid="b2b-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label={tr("dist.formBusiness")} required>
                <Input
                  value={b2bForm.business_name}
                  onChange={(e) => setB2bForm({ ...b2bForm, business_name: e.target.value })}
                  required
                  data-testid="b2b-business"
                  className="bg-transparent border-0 border-b border-[#E5E0D8] rounded-none focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0"
                />
              </Field>
              <Field label={tr("dist.formContact")} required>
                <Input
                  value={b2bForm.contact_name}
                  onChange={(e) => setB2bForm({ ...b2bForm, contact_name: e.target.value })}
                  required
                  data-testid="b2b-contact"
                  className="bg-transparent border-0 border-b border-[#E5E0D8] rounded-none focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0"
                />
              </Field>
              <Field label={tr("dist.formEmail")} required>
                <Input
                  type="email"
                  value={b2bForm.email}
                  onChange={(e) => setB2bForm({ ...b2bForm, email: e.target.value })}
                  required
                  data-testid="b2b-email"
                  className="bg-transparent border-0 border-b border-[#E5E0D8] rounded-none focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0"
                />
              </Field>
              <Field label={tr("dist.formPhone")}>
                <Input
                  value={b2bForm.phone}
                  onChange={(e) => setB2bForm({ ...b2bForm, phone: e.target.value })}
                  data-testid="b2b-phone"
                  className="bg-transparent border-0 border-b border-[#E5E0D8] rounded-none focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0"
                />
              </Field>
              <Field label={tr("dist.formNif")}>
                <Input
                  value={b2bForm.nif}
                  onChange={(e) => setB2bForm({ ...b2bForm, nif: e.target.value })}
                  data-testid="b2b-nif"
                  className="bg-transparent border-0 border-b border-[#E5E0D8] rounded-none focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0"
                />
              </Field>
              <Field label={tr("dist.formVolume")}>
                <Input
                  value={b2bForm.estimated_volume}
                  onChange={(e) => setB2bForm({ ...b2bForm, estimated_volume: e.target.value })}
                  data-testid="b2b-volume"
                  className="bg-transparent border-0 border-b border-[#E5E0D8] rounded-none focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0"
                />
              </Field>
            </div>
            <Field label={tr("dist.formAddress")}>
              <Input
                value={b2bForm.address}
                onChange={(e) => setB2bForm({ ...b2bForm, address: e.target.value })}
                data-testid="b2b-address"
                className="bg-transparent border-0 border-b border-[#E5E0D8] rounded-none focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0"
              />
            </Field>
            <Field label={tr("dist.formMessage")}>
              <Textarea
                rows={3}
                value={b2bForm.message}
                onChange={(e) => setB2bForm({ ...b2bForm, message: e.target.value })}
                data-testid="b2b-message"
                className="bg-transparent border-0 border-b border-[#E5E0D8] rounded-none focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0 resize-none"
              />
            </Field>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#1A1A1A] text-[#FDFBF7] hover:bg-black rounded-none px-10 py-6 text-[11px] uppercase tracking-[0.3em] w-full md:w-auto"
              data-testid="b2b-submit"
            >
              {submitting ? "..." : tr("dist.formSubmit")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}

function ProductCard({ p, lang, tr }) {
  const [open, setOpen] = useState(false);
  return (
    <article className="group" data-testid={`product-${p.id}`}>
      <div className="relative overflow-hidden aspect-[4/5] bg-[#EAE5DC]">
        <img
          src={resolveImageUrl(p.image_url) || FALLBACK_IMG}
          alt={lang === "pt" ? p.name_pt : p.name_en}
          onError={(e) => { if (e.currentTarget.src !== FALLBACK_IMG) e.currentTarget.src = FALLBACK_IMG; }}
          className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105"
        />
      </div>
      <div className="mt-5 flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#8C7A6B]">{p.category}</div>
          <h3 className="font-serif text-2xl text-[#1A1A1A] mt-1">{lang === "pt" ? p.name_pt : p.name_en}</h3>
          <div className="text-sm text-[#1A1A1A]/60 mt-1">{p.unit} · {p.origin}</div>
        </div>
        <div className="text-right">
          <div className="font-mono text-base text-[#1A1A1A]">€ {Number(p.price_retail).toFixed(2)}</div>
          <div className="font-mono text-xs text-[#8C7A6B]">{tr("dist.wholesale")} € {Number(p.price_wholesale).toFixed(2)}</div>
        </div>
      </div>
      <p className="mt-3 text-sm text-[#1A1A1A]/70 line-clamp-2">
        {lang === "pt" ? p.description_pt : p.description_en}
      </p>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button
            className="mt-4 text-[11px] uppercase tracking-[0.25em] border-b border-[#1A1A1A] pb-0.5 hover:text-[#8C7A6B] hover:border-[#8C7A6B] transition-colors"
            data-testid={`product-more-${p.id}`}
          >
            {lang === "pt" ? "Ver detalhes" : "View details"}
          </button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl bg-[#FDFBF7] rounded-none border border-[#1A1A1A]">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl font-light text-[#1A1A1A]">
              {lang === "pt" ? p.name_pt : p.name_en}
            </DialogTitle>
            <DialogDescription className="text-[11px] uppercase tracking-[0.25em] text-[#8C7A6B]">
              {p.category} · {p.unit} · {p.origin}
            </DialogDescription>
          </DialogHeader>
          {p.image_url && (
            <img
              src={resolveImageUrl(p.image_url)}
              alt=""
              onError={(e) => { if (e.currentTarget.src !== FALLBACK_IMG) e.currentTarget.src = FALLBACK_IMG; }}
              className="w-full h-72 object-cover"
            />
          )}
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#8C7A6B] sr-only">{p.category} · {p.unit} · {p.origin}</div>
          <p className="text-base text-[#1A1A1A]/80 leading-relaxed">{lang === "pt" ? p.description_pt : p.description_en}</p>
          {p.ingredients && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#8C7A6B] mb-1">{tr("dist.ingredients")}</div>
              <div className="text-sm text-[#1A1A1A]/80">{p.ingredients}</div>
            </div>
          )}
          <div className="flex justify-between border-t border-[#E5E0D8] pt-4 font-mono">
            <div><span className="text-[#8C7A6B] text-xs uppercase tracking-widest mr-2">{tr("dist.retail")}</span> € {Number(p.price_retail).toFixed(2)}</div>
            <div><span className="text-[#8C7A6B] text-xs uppercase tracking-widest mr-2">{tr("dist.wholesale")}</span> € {Number(p.price_wholesale).toFixed(2)}</div>
          </div>
        </DialogContent>
      </Dialog>
    </article>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-[0.25em] text-[#8C7A6B]">
        {label}{required && <span className="text-[#8C7A6B]"> *</span>}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
